import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { QuickFillHooks } from '@frontend/api-quick-fill';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { formatDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { getFullName } from '@frontend/name';
import { useAppScopeStore } from '@frontend/scope';
import { FormFieldActionTypes, useAlert, useForm } from '@frontend/design-system';
import {
  DATETIME_FORMATS,
  INITIAL_DIRECT_BOOKING_MESSAGE_TEMPLATE,
  WAITLIST_MIN_SLOT_DURATION_MINUTES,
} from '../../constants';
import { getMinTimeRoundedToNext15Minutes, getTodaysDateInTimezone } from '../../helpers';
import { useSendWaitlistMessage } from '../../hooks/use-send-waitlist-message';
import { useWaitlistShallowStore } from '../../stores/waitlist.store';
import { DropdownOption } from '../../types';
import { useWaitlistTrayContext } from '../../views/waitlist-tray-content/context';

export const apptDetailsFormFields = {
  date1: { type: 'date', value: '', required: true, minDate: '' },
  time1: { type: 'time', value: '', required: true },
  date2: { type: 'date', value: '', minDate: '' },
  time2: { type: 'time', value: '' },
  date3: { type: 'date', value: '', minDate: '' },
  time3: { type: 'time', value: '' },
  directScheduling: { type: 'switch', value: true },
  appointmentType: { type: 'dropdown', value: '' },
  provider: { type: 'dropdown', value: '' },
} as const;

export type WaitlistSlotAvailabilityStatus = 'not-required' | 'available' | 'unavailable' | 'checking' | 'error';

export type ApptDetailsFormFieldsName = keyof typeof apptDetailsFormFields;
export type WaitlistSlotInfo = {
  startDateTime: string;
  endDateTime: string;
};

const getDirectBookingMessageText = (startDateTime: string, locationTimezone?: string, timezoneSuffix = '') => {
  const dayjsInstance = locationTimezone ? dayjs(startDateTime).tz(locationTimezone) : dayjs(startDateTime);
  const formattedDateTime = dayjsInstance.format(DATETIME_FORMATS.WAITLIST_INVITE_DATETIME_DISPLAY);
  const dateTimeWithTimezone = timezoneSuffix ? `${formattedDateTime} ${timezoneSuffix}` : formattedDateTime;
  return INITIAL_DIRECT_BOOKING_MESSAGE_TEMPLATE.replaceAll('[Date Time]', dateTimeWithTimezone);
};

export const useWaitlistAppointmentDetailsForm = () => {
  const { t } = useTranslation('schedule-quick-fill');
  const alert = useAlert();
  const { accessibleLocationData } = useAppScopeStore();
  const {
    selectedProviderId: initialProviderId,
    selectedDate: initialDate,
    selectedTime: initialTime,
  } = useWaitlistTrayContext();
  const { isWaitlistDirectBookingEnabled } = QuickFillHooks.useGetWaitlistFeatureFlagDetails();

  const { setCurrentStep, selectedLocationId, selectedPersonIds, resetUserSelections } = useWaitlistShallowStore(
    'setCurrentStep',
    'selectedLocationId',
    'selectedPersonIds',
    'resetUserSelections'
  );

  const { timezoneAbbr, locationTimezone, locationId } = useMemo(() => {
    if (!selectedLocationId) return { timezoneAbbr: '', locationTimezone: '', locationId: '' };
    const locationTz = accessibleLocationData?.[selectedLocationId]?.timezone;
    return {
      timezoneAbbr: locationTz ? SchedulerV3.getTimezoneAbbreviation(locationTz) : '',
      locationTimezone: locationTz,
      locationId: selectedLocationId || '',
    };
  }, [accessibleLocationData, selectedLocationId]);

  const { handleSendMessage } = useSendWaitlistMessage();

  const sendInvitationMutation = SchedulerV3.Mutations.useSendWaitlistInvitationsMutation();
  const [activeSlotCount, setActiveSlotCount] = useState(1);

  const form = useForm({
    fields: {
      date1: {
        type: 'date',
        value: initialDate ? formatDate(initialDate, DATETIME_FORMATS.US_DATE) : '',
        required: true,
        minDate: getTodaysDateInTimezone(locationTimezone),
      },
      time1: {
        type: 'time',
        value: initialTime || '',
        required: true,
      },
      date2: {
        type: 'date',
        value: '',
        minDate: getTodaysDateInTimezone(locationTimezone),
      },
      time2: {
        type: 'time',
        value: '',
      },
      date3: {
        type: 'date',
        value: '',
        minDate: getTodaysDateInTimezone(locationTimezone),
      },
      time3: {
        type: 'time',
        value: '',
      },
      directScheduling: {
        type: 'switch',
        value: isWaitlistDirectBookingEnabled ? true : false,
      },
      provider: {
        type: 'dropdown',
        value: initialProviderId || '',
      },
      appointmentType: {
        type: 'dropdown',
        value: '',
      },
    },
    fieldStateReducer(state, action) {
      if (action.type === FormFieldActionTypes.Update && action.payload.name === 'directScheduling') {
        if (action.payload.value) {
          return {
            ...state,
            provider: {
              ...state.provider,
              required: true,
            },
            appointmentType: {
              ...state.appointmentType,
              required: true,
            },
          };
        } else {
          return {
            ...state,
            provider: {
              ...state.provider,
              value: '',
              error: '',
            },
            appointmentType: {
              ...state.appointmentType,
              value: '',
              error: '',
            },
          };
        }
      }
      // Handle date field changes for all slots
      if (action.type === FormFieldActionTypes.Update && action.payload.name?.startsWith('date')) {
        const todaysDate = getTodaysDateInTimezone(locationTimezone, 'MM/DD/YYYY');
        const isTodaySelected = action.payload.value === todaysDate;
        const minTime = isTodaySelected ? getMinTimeRoundedToNext15Minutes(locationTimezone) : '';

        // Get corresponding time field
        const slotNumber = action.payload.name.replace('date', '');
        const timeFieldName = `time${slotNumber}` as ApptDetailsFormFieldsName;
        const timeFieldState = state[timeFieldName];
        const currentTimeValue = timeFieldState?.value || '';
        const isCurrentTimeExceedingMinTime = minTime ? currentTimeValue < minTime : false;

        return {
          ...state,
          [timeFieldName]: {
            ...timeFieldState,
            touched: isCurrentTimeExceedingMinTime ? true : timeFieldState?.touched || false,
            error: isCurrentTimeExceedingMinTime ? t('Selected time has already passed') : timeFieldState?.error || '',
            minTime: isTodaySelected ? getMinTimeRoundedToNext15Minutes(locationTimezone) : '',
          },
        };
      }
      return state;
    },
  });

  const { data: appointmentTypesData } = SchedulerV3.Helpers.useGetAppointmentTypesV3Data({
    selectedLocationId: locationId,
    selectedLocationIds: [locationId],
    pageConfig: { page: 1, limit: SchedulerV3.Constants.CALENDAR_VIEW_V3_API_QUERY_LIMIT },
    isEnabled: form.values.directScheduling,
  });
  const { data: providersData } = SchedulerV3.Helpers.useGetProvidersV3Data({
    selectedLocationId: locationId,
    selectedLocationIds: [locationId],
    pageConfig: { page: 1, limit: SchedulerV3.Constants.CALENDAR_VIEW_V3_API_QUERY_LIMIT },
    isEnabled: form.values.directScheduling,
  });

  const providerDropdownOptions = useMemo<DropdownOption[]>(() => {
    return (providersData?.providers ?? [])
      .filter((provider) => !provider.isArchived && !provider.hidden)
      .map((provider) => ({
        label: getFullName(provider) || provider.displayName || provider.externalName || '',
        value: provider.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [providersData?.providers]);
  const appointmentTypeDropdownOptions = useMemo<DropdownOption[]>(() => {
    return (appointmentTypesData?.appointmentTypes ?? [])
      .filter((apptType) => apptType.details?.act)
      .map((appointmentType) => ({
        label: appointmentType.displayName || appointmentType.externalName || '',
        value: appointmentType.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [appointmentTypesData?.appointmentTypes]);

  const waitlistAvailabilityRequestParams = useMemo<SchedulerV3.Types.ListWaitlistAvailabilitiesIO['input']>(() => {
    // keep form complete check to avoid invalid date parsing
    const selectedDate =
      form.values.date1 && form.isComplete
        ? dayjs.tz(form.values.date1, DATETIME_FORMATS.US_DATE, locationTimezone)
        : null;
    const startDateTime = selectedDate ? selectedDate.format(DATETIME_FORMATS.ISO_DATETIME_WITH_OFFSET) : '';
    const endDateTime = selectedDate ? selectedDate.endOf('day').format(DATETIME_FORMATS.ISO_DATETIME_WITH_OFFSET) : '';
    return {
      locationId,
      providerId: form.values.provider || '',
      startDateTime,
      endDateTime,
      durationMinutes:
        appointmentTypesData?.appointmentTypes.find((type) => type.id === form.values.appointmentType)?.details?.dur ??
        WAITLIST_MIN_SLOT_DURATION_MINUTES,
    };
  }, [
    form.isComplete,
    form.values.appointmentType,
    form.values.provider,
    form.values.date1,
    locationId,
    locationTimezone,
    providersData?.providers,
    appointmentTypesData?.appointmentTypes,
  ]);
  const isCheckAvailabilityEnabled = !!(
    form.values.directScheduling &&
    waitlistAvailabilityRequestParams.locationId &&
    waitlistAvailabilityRequestParams.providerId &&
    form.values.appointmentType &&
    waitlistAvailabilityRequestParams.startDateTime &&
    waitlistAvailabilityRequestParams.endDateTime &&
    waitlistAvailabilityRequestParams.durationMinutes
  );

  const availabilityQuery = SchedulerV3.Queries.useListWaitlistAvailabilitiesQuery({
    request: waitlistAvailabilityRequestParams,
    options: {
      enabled: isCheckAvailabilityEnabled,
    },
  });

  const isSlotAvailable = useMemo(() => {
    if (!isCheckAvailabilityEnabled) return false;
    if (!form.values.time1) return false;

    const availableSlots = availabilityQuery.data?.availableSlots || [];
    if (!availableSlots.length) return false;

    const durationMinutes = waitlistAvailabilityRequestParams.durationMinutes || WAITLIST_MIN_SLOT_DURATION_MINUTES;
    const selectedStartTime = dayjs(form.values.time1, 'HH:mm:ss').format('HH:mm');
    const selectedEndTime = dayjs(form.values.time1, 'HH:mm:ss').add(durationMinutes, 'minutes').format('HH:mm');

    // Collect all individual slots and sort them
    const allSlots = availableSlots
      .flatMap(({ slots }) => slots || [])
      .filter((slot): slot is typeof slot & { startTime: string; endTime: string } =>
        Boolean(slot.startTime && slot.endTime)
      )
      .sort((a, b) => a.startTime.localeCompare(b.startTime));

    if (allSlots.length === 0) return false;

    // Merge consecutive slots into continuous blocks
    const continuousBlocks: Array<{ startTime: string; endTime: string }> = [];
    let currentBlock = { startTime: allSlots[0].startTime, endTime: allSlots[0].endTime };

    for (let i = 1; i < allSlots.length; i++) {
      const slot = allSlots[i];

      // If this slot starts exactly when the current block ends, extend the block
      if (slot.startTime === currentBlock.endTime) {
        currentBlock.endTime = slot.endTime;
      } else {
        // Gap found, save current block and start a new one
        continuousBlocks.push({ ...currentBlock });
        currentBlock = { startTime: slot.startTime, endTime: slot.endTime };
      }
    }
    // Don't forget to add the last block
    continuousBlocks.push(currentBlock);

    // Check if selected time range fits within any continuous block
    return continuousBlocks.some((block) => selectedStartTime >= block.startTime && selectedEndTime <= block.endTime);
  }, [
    isCheckAvailabilityEnabled,
    availabilityQuery.data,
    form.values.time1,
    waitlistAvailabilityRequestParams.durationMinutes,
  ]);

  const availabilityStatus = useMemo<WaitlistSlotAvailabilityStatus>(() => {
    if (!isCheckAvailabilityEnabled) return 'not-required';
    if (availabilityQuery.isLoading) return 'checking';
    if (availabilityQuery.isError) return 'error';
    return isSlotAvailable ? 'available' : 'unavailable';
  }, [isCheckAvailabilityEnabled, availabilityQuery.isError, availabilityQuery.isLoading, isSlotAvailable]);

  const handleBackClick = () => setCurrentStep('select-persons');
  const handleSendMessageClick = async () => {
    // Validate that at least the first slot is complete
    const hasRequiredSlot = form.values.date1 && form.values.time1;
    const isFormValid =
      hasRequiredSlot && (form.values.directScheduling ? form.values.provider && form.values.appointmentType : true);

    if (!isFormValid) {
      form.validate();
      return;
    }

    // Check availability for direct scheduling
    if (form.values.directScheduling) {
      if (availabilityQuery.isLoading) {
        alert.error(t('Checking availability. Please wait a moment and try again.'));
        return;
      }
      if (!isSlotAvailable) {
        alert.error(t('No availability found for the selected time slot. Please choose a different time or provider.'));
        return;
      }
    }

    SchedulerV3.trackPendoEvent('waitlist_appointment_message_send_clicked', {
      selectedPersonIds,
      appointmentLocationId: locationId,
      date: form.values.date1 ?? '',
      time: form.values.time1 ?? '',
      directBooking: form.values.directScheduling ?? false,
      providerId: form.values.provider ?? '',
      appointmentTypeId: form.values.appointmentType ?? '',
    });

    try {
      // Collect all filled appointment slots
      const appointmentSlots: WaitlistSlotInfo[] = [];
      for (let i = 1; i <= activeSlotCount; i++) {
        const dateValue = form.values[`date${i}` as ApptDetailsFormFieldsName];
        const timeValue = form.values[`time${i}` as ApptDetailsFormFieldsName];

        if (dateValue && timeValue) {
          const startDateTime = dayjs.tz(`${dateValue} ${timeValue}`, 'MM/DD/YYYY HH:mm:ss', locationTimezone);
          const durationMinutes =
            appointmentTypesData?.appointmentTypes.find((type) => type.id === form.values.appointmentType)?.details
              ?.dur ?? WAITLIST_MIN_SLOT_DURATION_MINUTES;

          appointmentSlots.push({
            startDateTime: startDateTime.format(DATETIME_FORMATS.ISO_DATETIME_WITH_OFFSET),
            endDateTime: startDateTime.add(durationMinutes, 'minute').format(DATETIME_FORMATS.ISO_DATETIME_WITH_OFFSET),
          });
        }
      }

      if (!form.values.directScheduling) {
        // If direct scheduling is not enabled, send invitations without appointment slots
        handleSendMessage(appointmentSlots, locationTimezone ?? '', timezoneAbbr ?? '');
        SchedulerV3.trackPendoEvent('waitlist_appointment_message_sent_success', {
          selectedPersonIds,
          appointmentLocationId: locationId,
          date: form.values.date1 ?? '',
          time: form.values.time1 ?? '',
          directBooking: form.values.directScheduling ?? false,
          providerId: form.values.provider ?? '',
          appointmentTypeId: form.values.appointmentType ?? '',
        });
        return;
      }

      // For now, send invitation for the first slot (API might need to be updated for multiple slots)
      if (appointmentSlots.length > 0) {
        await sendInvitationMutation.mutateAsync({
          locationId: selectedLocationId ?? '',
          sourceTenantId: selectedLocationId ?? '',
          personIds: selectedPersonIds,
          appointmentTypeId: form.values.appointmentType,
          providerId: form.values.provider,
          startDateTime: appointmentSlots[0].startDateTime,
          endDateTime: appointmentSlots[0].endDateTime,
          textBody: getDirectBookingMessageText(appointmentSlots[0].startDateTime, locationTimezone, timezoneAbbr),
        });
        SchedulerV3.trackPendoEvent('waitlist_appointment_message_sent_success', {
          selectedPersonIds,
          appointmentLocationId: locationId,
          date: form.values.date1 ?? '',
          time: form.values.time1 ?? '',
          directBooking: form.values.directScheduling ?? false,
          providerId: form.values.provider ?? '',
          appointmentTypeId: form.values.appointmentType ?? '',
        });
        alert.success('Invitations sent successfully');
        resetUserSelections();
      }
    } catch (error) {
      alert.error('Failed to send invitations. Please try again.');
    }
  };

  return {
    form,
    timezoneAbbr,
    isSendingInvitation: sendInvitationMutation.isLoading,
    providerDropdownOptions,
    appointmentTypeDropdownOptions,
    handleBackClick,
    handleSendMessageClick,
    activeSlotCount,
    setActiveSlotCount,
    availabilityStatus,
    isWaitlistDirectBookingEnabled,
  };
};
